/**
 * vue-cesium - https://github.com/zouyaoji/vue-cesium
 * Vue 2.x components for CesiumJS.
 *
 * @package VueCesium
 * @author zouyaoji <370681295@qq.com>
 * @version 2.0.6
 * @license MIT
 * @homepage https://zouyaoji.top/vue-cesium
 * @copyright (c) 2018-2020, zouyaoji <370681295@qq.com>
 */
import _Object$assign from '@babel/runtime-corejs2/core-js/object/assign';

var checkType = function checkType(val) {
  return Object.prototype.toString.call(val).slice(8, -1);
};
var toKebabCase = function toKebabCase(str) {
  return str.replace(/[A-Z]/g, function (letter) {
    return "-".concat(letter.toLowerCase());
  }).replace(/^-/, '');
};
/**
 * 通过 class 名获取 Dom 元素。
 * @param {Array<Element>} htmlCollection Dom元素集合。
 * @param {String} className class 名称。
 */

var getDocumentByClassName = function getDocumentByClassName(htmlCollection, className) {
  var temp;
  var BreakException = {};

  try {
    Array.prototype.slice.call(htmlCollection).forEach(function (element) {
      if (element.className === className) {
        temp = element;
        throw BreakException;
      }
    });
  } catch (e) {
    if (e !== BreakException) throw e;
  }

  return temp;
};
/**
 * 判断传入的对象是否是方法。
 * @param {*} value
 * @returns {Boolean}
 */

function isFunction(value) {
  return typeof value === 'function';
}
/**
 * 验证是否是经纬度。
 * @param {Number} longitude
 * @param {Number} latitude
 * @returns {Boolean}
 */

function lnglatValidator(longitude, latitude) {
  // 经度，整数部分为0-180小数部分为0到6位
  var longreg = /^(-|\+)?(((\d|[1-9]\d|1[0-7]\d|0{1,3})\.\d{0,15})|(\d|[1-9]\d|1[0-7]\d|0{1,3})|180\.0{0,15}|180)$/;

  if (!longreg.test(longitude)) {
    return false;
  } // 纬度,整数部分为0-90小数部分为0到6位


  var latreg = /^(-|\+)?([0-8]?\d{1}\.\d{0,15}|90\.0{0,15}|[0-8]?\d{1}|90)$/;

  if (!latreg.test(latitude)) {
    return false;
  }

  return true;
}
/**
 * 普通对象 {x: number, y: number } 转换为 Cesium.Cartesian2 对象
 * @param {Object} val
 * @returns {Object}
 */

function makeCartesian2(val) {
  return val && new Cesium.Cartesian2(val.x, val.y);
}
/**
 * 普通对象 {x: number, y: number, z: number } 转换为 Cesium.Cartesian3 对象
 * @param {Object} val
 * @returns {Object}
 */

function makeCartesian3(val) {
  if (val && Object.prototype.hasOwnProperty.call(val, 'x')) {
    return new Cesium.Cartesian3(val.x, val.y, val.z);
  } else if (val && Object.prototype.hasOwnProperty.call(val, 'lng')) {
    return Cesium.Cartesian3.fromDegrees(val.lng, val.lat, val.height);
  }

  return val;
}
/**
 * 普通数组 [lng, lat, height, ……，lng, lat, height] 转换为 Cesium.Cartesian3 数组
 * @param {Array} val
 * @returns {Array<Cartesian3>}
 */

function makeCartesian3Array(vals) {
  if (vals && vals instanceof Array && vals[0] instanceof Cesium.Cartesian3) {
    return vals;
  }

  var coordinates = [];
  vals.forEach(function (item) {
    coordinates.push(item.lng);
    coordinates.push(item.lat);
    coordinates.push(item.height);
  });
  return coordinates.length >= 3 ? Cesium.Cartesian3.fromDegreesArrayHeights(coordinates) : vals;
}
/**
 * 普通数组 [lng, lat, ……，lng, lat] 转换为 Cesium.Cartesian2 数组
 * @param {Array} vals
 * @returns {Array<Cartesian2>}
 */

function makeCartesian2Array(vals) {
  var cartesian2Array = [];
  vals.forEach(function (item) {
    cartesian2Array.push(new Cesium.Cartesian2(item.x, item.y));
  });
  return cartesian2Array;
}
/**
 *
 * @param {Object} val
 */

function makeQuaternion(val) {
  return val.x ? new Cesium.Quaternion(val.x, val.y, val.z, val.w) : val;
}
/**
 * 解析 HierarchyJson
 * @param {Object} val
 */

function parsePolygonHierarchyJson(val) {
  val.forEach(function (element) {
    element.positions = makeCartesian3Array(element.positions);

    if (element.holes) {
      parsePolygonHierarchyJson(element.holes);
    }
  });
}
/**
 * 普通数组或对象转 Cesium.PolygonHierarchy 对象。
 * @param {Object|Array} val
 */


function makePolygonHierarchy(val) {
  if (val instanceof Array && val.length >= 3) {
    return new Cesium.PolygonHierarchy(makeCartesian3Array(val));
  }

  if (Cesium.defined(val.positions)) {
    val.positions = makeCartesian3Array(val.positions);
    parsePolygonHierarchyJson(val.holes);
  }

  return val;
}
/**
 * 普通对象 {near: number, nearValue: number, far: number, farValue: number} 转 Cesium.NearFarScalar 对象。
 * @param {Object} val
 * @returns {NearFarScalar}
 */

function makeNearFarScalar(val) {
  return val && new Cesium.NearFarScalar(val.near, val.nearValue, val.far, val.farValue);
}
/**
 * 普通对象 {near: number, far: number} 转 Cesium.DistanceDisplayCondition 对象。
 * @param {Object} val
 * @returns {DistanceDisplayCondition}
 */

function makeDistanceDisplayCondition(val) {
  return val && new Cesium.DistanceDisplayCondition(val.near, val.far);
}
/**
 * 普通对象或数组 [r, g, b, a] 或字符串转 Cesium.Color 对象。
 * @param {String|Array|Object} val
 * @returns {Color}
 */

function makeColor(val) {
  if (val instanceof Cesium.Color) {
    return val;
  } else if (val instanceof Array) {
    return new Cesium.Color(val[0], val[1], val[2], val[3]);
  } else if (typeof val === 'string') {
    return Cesium.Color.fromCssColorString(val);
  }

  return val;
}
/**
 * 普通对象或数组 [r, g, b, a] 或字符串转 Material
 * @param {String|Array|Object} val
 */

function makeMaterial(val) {
  if (val instanceof Array || typeof val === 'string' && !/(.*)\.(jpg|bmp|gif|ico|pcx|jpeg|tif|png|raw|tga)$/.test(val)) {
    return makeColor(val);
  } // else if (val && val.hasOwnProperty('fabric')) {
  //   return new Cesium.Material({
  //     fabric: {
  //       type: 'Color',
  //       uniforms: {
  //         color: new Cesium.Color(1.0, 1.0, 0.0, 1.0)
  //       }
  //     }
  //   })
  // }


  return val;
}
/**
 * 普通对象 {west: number, south: number, east: number, north: number} 转 Cesium.Rectangle 对象。
 * @param {Object} val
 * @returns {Rectangle}
 */

function makeRectangle(val) {
  // Entiy 的 rectangle 属性不能调用这个方法
  if (val instanceof Cesium.RectangleGraphics) {
    return val;
  }

  return val && Cesium.Rectangle.fromDegrees(val.west, val.south, val.east, val.north);
}
/**
 * 普通对象 {x: number, y: number, width: number, height: number} 转 Cesium.BoundingRectangle 对象。
 * @param {Object} val
 * @returns {BoundingRectangle}
 */

function makeBoundingRectangle(val) {
  return val && new Cesium.BoundingRectangle(val.x, val.y, val.width, val.height);
}
/**
 * 普通对象 {normal: number, distance: number} 转 Cesium.Plane 对象。
 * @param {Object} val
 * @returns {Plane}
 */

function makePlane(val) {
  // Entiy 和 PlaneGraphics 都有个 plane 属性 要区别一下
  if (val instanceof Cesium.PlaneGraphics) {
    return val;
  }

  if (val) {
    Cesium.Cartesian3.normalize(makeCartesian3(val.normal), val.normal);
    return new Cesium.Plane(val.normal, val.distance);
  }

  return val;
}
/**
 * 普通对象转平移、旋转、缩放变换对象。
 * @param {*} val
 */

function makeTranslationRotationScale(val) {
  return val && new Cesium.TranslationRotationScale(makeCartesian3(val.translation), makeQuaternion(val.rotation), makeCartesian3(val.scale));
}
function makeOptions(val) {
  var cmpName = this.$options.name;

  switch (cmpName) {
    case 'vc-datasource-geojson':
      var result = {};

      _Object$assign(result, val);

      result && result.markerColor && (result.markerColor = makeColor(result.markerColor));
      result && result.stroke && (result.stroke = makeColor(result.stroke));
      result && result.fill && (result.fill = makeColor(result.fill));
      return result;
  }

  return val;
}
function dirname(path) {
  if (typeof path !== 'string') path = path + '';
  if (path.length === 0) return '.';
  var code = path.charCodeAt(0);
  var hasRoot = code === 47;
  /* / */

  var end = -1;
  var matchedSlash = true;

  for (var i = path.length - 1; i >= 1; --i) {
    code = path.charCodeAt(i);

    if (code === 47
    /* / */
    ) {
        if (!matchedSlash) {
          end = i;
          break;
        }
      } else {
      // We saw the first non-path separator
      matchedSlash = false;
    }
  }

  if (end === -1) return hasRoot ? '/' : '.';

  if (hasRoot && end === 1) {
    // return '//';
    // Backwards-compat fix:
    return '/';
  }

  return path.slice(0, end);
}
function Platform() {
  var ua = navigator.userAgent;
  var isWindowsPhone = /(?:Windows Phone)/.test(ua);
  var isSymbian = /(?:SymbianOS)/.test(ua) || isWindowsPhone;
  var isAndroid = /(?:Android)/.test(ua);
  var isFireFox = /(?:Firefox)/.test(ua);
  var isChrome = /(?:Chrome|CriOS)/.test(ua);
  var isTablet = /(?:iPad|PlayBook)/.test(ua) || isAndroid && !/(?:Mobile)/.test(ua) || isFireFox && /(?:Tablet)/.test(ua);
  var isPhone = /(?:iPhone)/.test(ua) && !isTablet;
  var isPc = !isPhone && !isAndroid && !isSymbian;
  return {
    isTablet: isTablet,
    isPhone: isPhone,
    isAndroid: isAndroid,
    isPc: isPc,
    isChrome: isChrome
  };
}
function captureScreenshot(viewer) {
  var _Cesium = Cesium,
      when = _Cesium.when;
  var deferred = when.defer();
  var scene = viewer.scene;
  var removeCallback = scene.postRender.addEventListener(function () {
    removeCallback();

    try {
      var cesiumCanvas = viewer.scene.canvas; // If we're using the splitter, draw the split position as a vertical white line.

      var canvas = cesiumCanvas; // if (showSplitter) {
      //   canvas = document.createElement('canvas')
      //   canvas.width = cesiumCanvas.width
      //   canvas.height = cesiumCanvas.height
      //   const context = canvas.getContext('2d')
      //   context.drawImage(cesiumCanvas, 0, 0)
      //   const x = viewer.splitPosition * cesiumCanvas.width
      //   context.strokeStyle = this.terria.baseMapContrastColor
      //   context.beginPath()
      //   context.moveTo(x, 0)
      //   context.lineTo(x, cesiumCanvas.height)
      //   context.stroke()
      // }

      deferred.resolve(canvas.toDataURL('image/png'));
    } catch (e) {
      deferred.reject(e);
    }
  }, this);
  scene.render(viewer.clock.currentTime);
  return deferred.promise;
}
function getAllAttribution(viewer) {
  var credits = viewer.scene.frameState.creditDisplay._currentFrameCredits.screenCredits.values.concat(viewer.scene.frameState.creditDisplay._currentFrameCredits.lightboxCredits.values);

  return credits.map(function (credit) {
    return credit.html;
  });
}
function getExtension(fileName) {
  var start = fileName.lastIndexOf('.');

  if (start >= 0) {
    return fileName.substring(start, fileName.length);
  }

  return '';
}
function changeExtension(fname, newExt) {
  return fname.replace(getExtension(fname), newExt);
}
function readAsArrayBuffer(file) {
  var promise = Cesium.when.defer();
  var fr = new FileReader();

  fr.onload = function (e) {
    promise.resolve(e.target.result);
  };

  fr.onprogress = function (e) {
    promise.progress(e.target.result);
  };

  fr.onerror = function (e) {
    promise.reject(e.error);
  };

  fr.readAsArrayBuffer(file);
  return promise;
}
function readAsText(file) {
  var promise = Cesium.when.defer();
  var fr = new FileReader();

  fr.onload = function (e) {
    promise.resolve(e.target.result);
  };

  fr.onprogress = function (e) {
    promise.progress(e.target.result);
  };

  fr.onerror = function (e) {
    promise.reject(e.error);
  };

  fr.readAsText(file);
  return promise;
}
function readAllBytes(file) {
  var promise = Cesium.when.defer();
  var fr = new FileReader();

  fr.onload = function (e) {
    promise.resolve(new Uint8Array(e.target.result));
  };

  fr.onprogress = function (e) {
    promise.progress(e.target.result);
  };

  fr.onerror = function (e) {
    promise.reject(e.error);
  };

  fr.readAsArrayBuffer(file);
  return promise;
}
function getString(arrayBuffer, encoding) {
  if (!(arrayBuffer instanceof Uint8Array) && !(arrayBuffer instanceof ArrayBuffer) && arrayBuffer.buffer) {
    arrayBuffer = arrayBuffer.buffer;
  }

  var decoder = new TextDecoder(encoding);
  var decodedText = decoder.decode(arrayBuffer, {
    stream: true
  });
  return decodedText;
}

export { Platform, captureScreenshot, changeExtension, checkType, dirname, getAllAttribution, getDocumentByClassName, getExtension, getString, isFunction, lnglatValidator, makeBoundingRectangle, makeCartesian2, makeCartesian2Array, makeCartesian3, makeCartesian3Array, makeColor, makeDistanceDisplayCondition, makeMaterial, makeNearFarScalar, makeOptions, makePlane, makePolygonHierarchy, makeQuaternion, makeRectangle, makeTranslationRotationScale, readAllBytes, readAsArrayBuffer, readAsText, toKebabCase };
